import { Button } from "flowbite-react";
import { FaWhatsapp, FaMapMarkerAlt } from "react-icons/fa";
import { useState } from "react";
import { useSelector } from "react-redux";
import { getSessionId } from "../../../utils";
import { registrarAcao } from "../../../api/public";
import {
  buildLinkHref,
  getActionTypeByLinkType,
  getLinksByType,
  isUuid,
} from "../../../utils/storeLinks";
import LinkChoiceModal from "../LinkChoiceModal";

const ProdutoAcoes = ({ produto, variant = "default" }) => {
  const loja = useSelector((state) => state.catalogo.loja);
  const sessionId = getSessionId();
  const [pickerState, setPickerState] = useState({
    open: false,
    title: "",
    links: [],
    whatsappText: null,
    origem: "",
  });

  if (!loja) return null;

  const baseUrl = window.location.origin + window.location.pathname;
  const linkDoProduto = `${baseUrl}?produto=${produto.id}`;
  const whatsappMensagem = `Amei esse produto na sua Vintree! Gostaria de mais informacoes sobre ele:\n\n${produto.descricao}\n\nVeja aqui: ${linkDoProduto}`;

  const whatsappLinks = getLinksByType(loja, "whatsapp").filter((link) => {
    const href = buildLinkHref(link, { whatsappText: whatsappMensagem });
    if (!href) return false;

    // No card de produto, aceitamos apenas links de conversa direta.
    // Ex.: api.whatsapp.com/send, whatsapp.com/send e wa.me (ignora grupos chat.whatsapp.com).
    const normalizedHref = href.toLowerCase();
    if (normalizedHref.includes("chat.whatsapp.com/")) return false;

    return (
      normalizedHref.includes("wa.me/") ||
      normalizedHref.includes("api.whatsapp.com/send") ||
      normalizedHref.includes("whatsapp.com/send")
    );
  });
  const geolocalizacaoLinks = getLinksByType(loja, "geolocalizacao");
  const editorial = variant === "editorial";
  const whatsappButtonClass = editorial
    ? "w-full min-w-[12.5rem] justify-center whitespace-nowrap rounded-full border border-white/30 bg-white/15 px-6 text-white shadow-xl backdrop-blur-2xl hover:bg-white/25 focus:ring-white/40 sm:w-auto"
    : "w-full sm:w-auto justify-center rounded-full px-6";
  const locationButtonClass = editorial
    ? "w-full min-w-[9.5rem] justify-center whitespace-nowrap rounded-full border border-white/25 bg-black/15 px-6 text-white shadow-xl backdrop-blur-2xl hover:bg-white/15 focus:ring-white/40 sm:w-auto"
    : "w-full sm:w-auto justify-center rounded-full px-6";

  const trackLinkClick = (link, origem) => {
    const payload = {
      session_id: sessionId,
      loja_id: loja.id,
      tipo_acao: getActionTypeByLinkType(link?.tipo),
      origem: origem || produto.id,
      link_tipo: link?.tipo || null,
      link_label: link?.label || null,
      link_valor: link?.valor || null,
    };

    if (isUuid(link?.id)) {
      payload.loja_link_id = link.id;
    }

    registrarAcao(payload).catch((err) =>
      console.error("Erro ao registrar acao de link:", err)
    );
  };

  const openPicker = ({ title, links, whatsappText = null, origem = produto.id }) => {
    setPickerState({
      open: true,
      title,
      links,
      whatsappText,
      origem,
    });
  };

  const closePicker = () => {
    setPickerState({
      open: false,
      title: "",
      links: [],
      whatsappText: null,
      origem: "",
    });
  };

  const renderWhatsappAction = () => {
    if (!whatsappLinks.length) return null;

    if (whatsappLinks.length === 1) {
      const link = whatsappLinks[0];
      const href = buildLinkHref(link, { whatsappText: whatsappMensagem });
      if (!href) return null;

      return (
        <Button
          color={editorial ? "dark" : "success"}
          className={whatsappButtonClass}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackLinkClick(link, produto.id)}
        >
          <FaWhatsapp className="mr-2 text-lg" />
          Conversar no WhatsApp
        </Button>
      );
    }

    return (
      <Button
        color={editorial ? "dark" : "success"}
        className={whatsappButtonClass}
        onClick={() =>
          openPicker({
            title: "Escolha um WhatsApp",
            links: whatsappLinks,
            whatsappText: whatsappMensagem,
            origem: produto.id,
          })
        }
      >
        <FaWhatsapp className="mr-2 text-lg" />
        Conversar no WhatsApp
      </Button>
    );
  };

  const renderGeolocalizacaoAction = () => {
    if (!geolocalizacaoLinks.length) return null;

    if (geolocalizacaoLinks.length === 1) {
      const link = geolocalizacaoLinks[0];
      const href = buildLinkHref(link);
      if (!href) return null;

      return (
        <Button
          color={editorial ? "dark" : "gray"}
          className={locationButtonClass}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackLinkClick(link, `${produto.id}_localizacao`)}
        >
          <FaMapMarkerAlt className="mr-2 text-lg" />
          Visitar Loja
        </Button>
      );
    }

    return (
      <Button
        color={editorial ? "dark" : "gray"}
        className={locationButtonClass}
        onClick={() =>
          openPicker({
            title: "Escolha um endereco",
            links: geolocalizacaoLinks,
            origem: `${produto.id}_localizacao`,
          })
        }
      >
        <FaMapMarkerAlt className="mr-2 text-lg" />
        Escolher Endereco
      </Button>
    );
  };

  return (
    <>
      <div
        className={
          editorial
            ? "flex w-full flex-col gap-3 border-t border-white/20 pt-5 sm:flex-row lg:justify-end lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0"
            : "mt-4 flex flex-col gap-4 border-t border-gray-200 pt-4 sm:flex-row"
        }
      >
        {renderWhatsappAction()}
        {renderGeolocalizacaoAction()}
      </div>

      <LinkChoiceModal
        open={pickerState.open}
        onClose={closePicker}
        title={pickerState.title}
        links={pickerState.links}
        onSelect={(link) => {
          const href = buildLinkHref(link, {
            whatsappText: pickerState.whatsappText,
          });

          if (!href) return;

          trackLinkClick(link, pickerState.origem || produto.id);
          window.open(href, "_blank", "noopener,noreferrer");
          closePicker();
        }}
      />
    </>
  );
};

export default ProdutoAcoes;
